import { ListItemCard } from '@components/display/cards';
import { Flag, VictronImage } from '@components/display/images';
import { RegistrationEventListItem } from '@event/types';
import {
    getEventTypeShortLabel,
    getRegistrationLabel,
    getRegistrationStatus,
    getRegistrationStatusColor,
} from '@event/utils';
import GenericBadgeIcon from '@levelSystem/assets/generic-neutral-badge.svg?react';
import LanguageIcon from '@mui/icons-material/Language';
import { Avatar, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type OverviewEventCardProps = {
    loading?: boolean;
    data?: Pick<
        RegistrationEventListItem,
        | 'published'
        | 'max_capacity'
        | 'number_attendees'
        | 'subscription_status'
        | 'user_meets_level_badge_requirements'
        | 'country_code'
        | 'date_start'
        | 'date_end'
        | 'name'
        | 'type'
        | 'banner_image'
        | 'id'
    > & {
        language_spoken?: {
            code: string;
            label: string;
        };
        level_badge_requirements: any[];
        markets: any[];
    };
    hasUserData?: boolean;
    onMouseEnter?: () => void;
};

export const OverviewEventCard = ({ loading, data, hasUserData, onMouseEnter }: OverviewEventCardProps) => {
    //#region Hooks
    const intl = useIntl();
    //#endregion

    //#region Processed Data
    const registrationInfo = useMemo(() => {
        if (!data || !hasUserData) return null;
        const registrationStatus = getRegistrationStatus(data, hasUserData);

        if (!registrationStatus) return null;

        return {
            label: getRegistrationLabel(registrationStatus, intl),
            color: getRegistrationStatusColor(registrationStatus),
        };
    }, [data, hasUserData, intl]);

    const dateLabel = useMemo(() => {
        if (!data?.date_start || !data?.date_end) return null;

        const startDateLabel = format(data.date_start, 'dd MMM yyyy');
        const endDateLabel = format(data.date_end, 'dd MMM yyyy');

        return startDateLabel === endDateLabel ? startDateLabel : `${startDateLabel} - ${endDateLabel}`;
    }, [data?.date_start, data?.date_end]);

    const metadataItems = useMemo(() => {
        if (!data) return [];

        const items: React.ReactNode[] = [];

        if (data.language_spoken?.code) {
            items.push(
                <Stack key="language" direction="row" spacing="3px" alignItems="center">
                    <Avatar src={`/static/flags/${data.language_spoken.code}.svg`} className="w-[17px] h-[17px]" />
                    <Typography fontSize={16} color="neutral-550.main" lineHeight="24px" noWrap>
                        {data.language_spoken.label}
                    </Typography>
                </Stack>
            );
        }

        items.push(
            <Stack key="type" direction="row" spacing="3px" alignItems="center">
                <LanguageIcon sx={{ fill: (theme) => theme.palette['neutral-550'].main }} fontSize="small" />
                <Typography fontSize={16} color="neutral-550.main" lineHeight="24px" noWrap>
                    {getEventTypeShortLabel(data.type)}
                </Typography>
            </Stack>
        );

        if (data.level_badge_requirements.length) {
            items.push(
                <Stack key="badge" direction="row" alignItems="center" spacing="3px">
                    <GenericBadgeIcon />
                    <Typography fontSize={16} color="neutral-550.main" noWrap>
                        <FormattedMessage
                            id="events.labels.badgeRequired"
                            defaultMessage="Badge Required"
                            description="/app/events/{id} - Label indicating event requires a badge"
                        />
                    </Typography>
                </Stack>
            );
        }

        if (data.markets.length > 0) {
            items.push(
                <Stack key="markets" direction="row" spacing="3px" alignItems="center">
                    <Typography
                        fontSize="10.67px"
                        width="19px"
                        height="19px"
                        borderRadius="50%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        className="aspect-square"
                        flexShrink={0}
                        fontWeight={700}
                        color="white"
                        bgcolor={(theme) => theme.palette['primary-700'].main}
                    >
                        {data.markets.length > 9 ? '9+' : data.markets.length}
                    </Typography>
                    <Typography fontSize={16} color="neutral-550.main" lineHeight="24px" noWrap>
                        <FormattedMessage id="events.labels.relatedMarkets" defaultMessage="Related Markets" />
                    </Typography>
                </Stack>
            );
        }

        return items;
    }, [data]);
    //#endregion

    return (
        <ListItemCard
            loading={loading}
            image={
                <>
                    <VictronImage
                        src={data?.banner_image}
                        alt={`Banner of ${data?.name}`}
                        className={'h-full w-full rounded-md object-cover'}
                    />
                    <Flag
                        flag={data?.country_code}
                        className="absolute top-[6px] left-[5px] rounded w-[35px] h-[23px]"
                    />
                </>
            }
            chip={registrationInfo ?? undefined}
            dateLabel={dateLabel}
            title={data?.name}
            metadataItems={metadataItems}
            actionLabel={
                <FormattedMessage
                    id="common.actions.learnMore"
                    defaultMessage="Learn more"
                    description="Common button text to view more details"
                />
            }
            linkTo={data?.type && data?.id ? `events/${data.type}/${data.id}` : undefined}
            onMouseEnter={onMouseEnter}
        />
    );
};
